// Permission catalog metadata (labels & helpers) for the granular-permissions
// feature. The authoritative catalog comes from GET /api/permissions; these
// are display labels, ordering, and value helpers used across the UI.

// The 10 permission domains, in the order we like to present them.
export const PERMISSION_DOMAINS = [
  "dashboard",
  "orders",
  "cash_register",
  "payments",
  "inventory",
  "suppliers",
  "menu",
  "expenses",
  "reports",
  "customers",
  "settings",
  "employees",
] as const;

export type PermissionDomainKey = (typeof PERMISSION_DOMAINS)[number];

export const DOMAIN_LABELS: Record<string, string> = {
  dashboard: "Tableau de bord",
  orders: "Commandes",
  cash_register: "Caisse",
  payments: "Paiements",
  inventory: "Inventaire",
  suppliers: "Fournisseurs",
  menu: "Menu",
  expenses: "Dépenses",
  reports: "Rapports",
  customers: "Clients",
  settings: "Paramètres",
  employees: "Employés",
};

export const ACTION_LABELS: Record<string, string> = {
  view: "Consulter",
  manage: "Gérer",
  reports: "Rapports",
  open: "Ouvrir",
  close: "Clôturer",
  movement: "Mouvements",
  capture: "Encaisser",
  create: "Enregistrer",
  void: "Annuler",
  manage_categories: "Gérer les catégories",
};

// Stable display order for actions within a domain (view first).
export const ACTION_ORDER: Record<string, number> = {
  view: 0,
  manage: 1,
  reports: 2,
  capture: 3,
  create: 4,
  void: 5,
  manage_categories: 6,
  open: 7,
  movement: 8,
  close: 9,
};

export const domainLabel = (domain: string) => DOMAIN_LABELS[domain] ?? domain;
export const actionLabel = (action: string) => ACTION_LABELS[action] ?? action;

// Sorts catalog actions into a stable, view-first order.
export const sortActions = <T extends { action: string }>(perms: T[]): T[] =>
  [...perms].sort(
    (a, b) => (ACTION_ORDER[a.action] ?? 99) - (ACTION_ORDER[b.action] ?? 99)
  );

// Collapses raw permission values into per-domain action lists for compact
// display. When a domain has actions beyond view, view is dropped (every other
// action implies it). Actions come back in stable view-first order.
export const summarizePermissions = (
  permissions: string[]
): { domain: string; actions: string[] }[] => {
  const byDomain: Record<string, Set<string>> = {};
  permissions.forEach((value) => {
    const [domain, action] = value.split(".");
    if (!domain || !action) return;
    (byDomain[domain] ??= new Set()).add(action);
  });
  return PERMISSION_DOMAINS.filter((d) => byDomain[d]).map((domain) => {
    const actions = [...byDomain[domain]];
    const shown = actions.length > 1 ? actions.filter((a) => a !== "view") : actions;
    return {
      domain,
      actions: sortActions(shown.map((action) => ({ action }))).map(
        (x) => x.action
      ),
    };
  });
};

// Permission value helpers, e.g. permView("inventory") === "inventory.view".
export const perm = (domain: string, action: string) => `${domain}.${action}`;
export const permView = (domain: string) => perm(domain, "view");
export const permManage = (domain: string) => perm(domain, "manage");

// Recording purchases (the "Saisie des achats" form, "Nouveau bon",
// "Dupliquer") saves through suppliers.manage, and the form picks ingredients
// from the ingredients list, which needs inventory.view. A supplier-only user
// would get a form whose ingredient picker 403s, so both are required
// (SFRESTO-165). `can` is usePermissions().can.
export const canRecordPurchaseOrders = (
  can: (permission: string) => boolean
): boolean => can("suppliers.manage") && can("inventory.view");
